<template>
  <div class="bc-wizard">
    <div class="bc-wizard__head">
      <h4 class="bc-wizard__main-title">{{ msg('New building') }}</h4>
      <p class="bc-wizard__subtitle">{{ msg('Step') }} {{ step + 1 }} / 3 — {{ msg(stepMeta[step].label) }}</p>
    </div>

    <b-row class="bc-wizard__layout mx-0">
      <!-- Vertical step rail (desktop) -->
      <b-col cols="12" md="auto" class="bc-wizard__rail pl-0 pr-md-2 mb-2 mb-md-0">
        <div class="bc-wizard__rail-inner">
          <button
            v-for="(s, idx) in stepMeta"
            :key="s.key"
            type="button"
            class="bc-wizard__rail-item"
            :class="{ active: step === idx, done: step > idx }"
            :disabled="idx > step"
            @click="goToStep(idx)"
          >
            <span class="bc-wizard__rail-num">
              <feather-icon v-if="step > idx" icon="CheckIcon" size="12"/>
              <span v-else>{{ idx + 1 }}</span>
            </span>
            <span class="bc-wizard__rail-text">
              <span class="bc-wizard__rail-label">{{ msg(s.label) }}</span>
              <span class="bc-wizard__rail-hint">{{ msg(s.hint) }}</span>
            </span>
          </button>
        </div>
      </b-col>

      <b-col cols="12" md class="bc-wizard__main-col px-0 pl-md-2">
        <div class="bc-wizard__body">
      <!-- Step 1: Address & legal -->
      <validation-observer v-show="step === 0" ref="obs0" tag="div">
        <div class="bc-wizard__panel">
          <h5 class="bc-wizard__title">
            <feather-icon icon="MapPinIcon" size="16" class="mr-50"/>
            {{ msg('Location & details') }}
          </h5>

          <div class="bc-wizard__section">
            <div class="bc-wizard__section-label">{{ msg('Address') }}</div>
            <b-row class="bc-wizard__row">
              <b-col cols="12">
                <b-form-group :label="msg('Company')" label-for="bc-company" class="mb-1">
                  <b-form-input id="bc-company" v-model="building.company" size="sm" :placeholder="msg('Company name')"/>
                </b-form-group>
              </b-col>
              <b-col cols="12">
                <b-form-group :label="msg('Street address')" label-for="bc-address" class="mb-1">
                  <b-form-input id="bc-address" v-model="building.address" size="sm" :placeholder="msg('Street')"/>
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group :label="msg('Zip')" label-for="bc-zip" class="mb-1">
                  <b-form-input id="bc-zip" v-model="building.zip" size="sm"/>
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group :label="msg('City')" label-for="bc-loc" class="mb-1">
                  <b-form-input id="bc-loc" v-model="building.location" size="sm" :placeholder="msg('City')"/>
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <div class="bc-wizard__section mt-1">
            <div class="bc-wizard__section-label">{{ msg('Legal & building') }}</div>
            <b-row class="bc-wizard__row">
              <b-col cols="6">
                <b-form-group :label="msg('GST')" label-for="bc-gst" class="mb-1">
                  <validation-provider #default="{ errors }" :name="msg('GST')" rules="required">
                    <b-form-input id="bc-gst" v-model="building.gst" size="sm" :state="errors.length ? false : null"/>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group :label="msg('KG')" label-for="bc-kg" class="mb-1">
                  <validation-provider #default="{ errors }" :name="msg('KG')" rules="required">
                    <b-form-input id="bc-kg" v-model="building.kg" size="sm" :state="errors.length ? false : null"/>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group :label="msg('Usage')" label-for="bc-usage" class="mb-1">
                  <b-form-input id="bc-usage" v-model="building.usage" size="sm"/>
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group :label="msg('Total space')" label-for="bc-space" class="mb-1">
                  <b-form-input id="bc-space" v-model="building.space" size="sm"/>
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </div>
      </validation-observer>

      <!-- Step 2: Owner, branch, templates, taxes -->
      <validation-observer v-show="step === 1" ref="obs1" tag="div">
        <div class="bc-wizard__panel">
          <h5 class="bc-wizard__title">
            <feather-icon icon="UsersIcon" size="16" class="mr-50"/>
            {{ msg('Owner, branch & templates') }}
          </h5>

          <div class="bc-wizard__section">
            <div class="bc-wizard__section-label">{{ msg('People & branch') }}</div>
            <b-row class="bc-wizard__row">
              <b-col cols="12">
                <b-form-group :label="msg('Owner')" label-for="bc-owner" class="mb-1">
                  <validation-provider #default="{ errors }" :name="msg('Owner')" rules="required">
                    <client-picker v-model="building.ownerId" is-owner/>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col cols="12">
                <b-form-group :label="msg('Branch')" label-for="bc-branch" class="mb-1">
                  <validation-provider #default="{ errors }" :name="msg('Branch')" rules="required">
                    <v-select
                      id="bc-branch"
                      :dir="isRTL ? 'rtl' : 'ltr'"
                      v-model="building.buid"
                      :options="allBranches"
                      class="w-100 bc-wizard__vselect"
                      label="name"
                      :reduce="b => b.id"
                      :clearable="false"
                      @input="onBranchChange"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <div class="bc-wizard__section mt-1">
            <div class="bc-wizard__section-label">{{ msg('Templates & taxes') }}</div>
            <b-row class="bc-wizard__row">
              <b-col cols="12">
                <b-form-group :label="msg('Offer template')" label-for="bc-offer" class="mb-1">
                  <validation-provider #default="{ errors }" :name="msg('Offer template')" rules="required">
                    <v-select
                      id="bc-offer"
                      multiple
                      :dir="isRTL ? 'rtl' : 'ltr'"
                      v-model="building.offerTemplateIds"
                      :options="allTemplates"
                      class="w-100 bc-wizard__vselect"
                      label="name"
                      :reduce="t => t.id"
                      :clearable="false"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col cols="12" sm="6">
                <b-form-group :label="msg('Status')" label-for="bc-status" class="mb-1">
                  <validation-provider #default="{ errors }" :name="msg('Status')" rules="required">
                    <v-select
                      id="bc-status"
                      :dir="isRTL ? 'rtl' : 'ltr'"
                      :clearable="false"
                      v-model="building.status"
                      label="text"
                      :options="allStatus"
                      :reduce="s => s.value"
                      class="w-100 bc-wizard__vselect"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col cols="6" sm="3">
                <b-form-group :label="msg('City tax')" label-for="bc-ct" class="mb-1">
                  <validation-provider #default="{ errors }" :name="msg('City tax')" rules="required">
                    <b-form-input id="bc-ct" v-model.number="building.cityTax" type="number" min="0" step="0.1" size="sm"/>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col cols="6" sm="3">
                <b-form-group :label="msg('Vat')" label-for="bc-vat" class="mb-1">
                  <validation-provider #default="{ errors }" :name="msg('Vat')" rules="required">
                    <b-form-input id="bc-vat" v-model.number="building.vat" type="number" min="0" step="0.1" size="sm" placeholder="9.8"/>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </div>
      </validation-observer>

      <!-- Step 3: Link properties -->
      <div v-show="step === 2" class="bc-wizard__panel">
        <h5 class="bc-wizard__title">
          <feather-icon icon="HomeIcon" size="16" class="mr-50"/>
          {{ msg('Link properties') }}
        </h5>
        <p class="text-muted small mb-2">
          {{ msg('Only properties in the same branch with no building yet are listed. Each add is saved immediately.') }}
        </p>

        <div
          v-if="linkedSnapshot.length === 0 && !propertiesLoading && filteredAvailable.length === 0"
          class="bc-wizard__empty-hint small mb-2"
        >
          {{ msg('No unlinked properties in this branch, or all are already linked.') }}
        </div>

        <div v-if="linkedSnapshot.length" class="bc-wizard__linked mb-2">
          <div class="small text-muted mb-50">{{ msg('Linked to this building') }}</div>
          <div class="d-flex flex-wrap" style="gap:8px">
            <b-badge
              v-for="p in linkedSnapshot"
              :key="p.id"
              variant="light-primary"
              class="bc-wizard__chip d-flex align-items-center"
            >
              <span class="text-truncate" style="max-width:220px">{{ propertyLabel(p) }}</span>
              <feather-icon
                icon="XIcon"
                size="14"
                class="ml-50 cursor-pointer"
                :class="{ 'opacity-50': unlinkingId === p.id }"
                @click="unlinkProperty(p)"
              />
            </b-badge>
          </div>
        </div>

        <b-form-group :label="msg('Search')" class="mb-2">
          <b-form-input v-model="propertySearch" :placeholder="msg('Filter by name or address')" debounce="200"/>
        </b-form-group>

        <div v-if="propertiesLoading" class="text-center py-3">
          <b-spinner small/>
        </div>
        <div v-else class="bc-wizard__prop-list border rounded">
          <div
            v-for="p in filteredAvailable"
            :key="p.id"
            class="bc-wizard__prop-row d-flex align-items-center justify-content-between"
          >
            <div class="min-w-0">
              <div class="font-weight-bold text-truncate">{{ propertyLabel(p) }}</div>
              <small class="text-muted" v-if="p.address">{{ formatAddress(p) }}</small>
            </div>
            <b-button
              size="sm"
              variant="primary"
              :disabled="linkingId === p.id"
              @click="linkProperty(p)"
            >
              <b-spinner v-if="linkingId === p.id" small class="mr-50"/>
              <feather-icon v-else icon="PlusIcon" size="14" class="mr-25"/>
              {{ msg('Add') }}
            </b-button>
          </div>
          <div v-if="!filteredAvailable.length && !propertiesLoading" class="p-2 text-muted small text-center">
            {{ msg('No matching properties') }}
          </div>
        </div>
      </div>
        </div>
      </b-col>
    </b-row>

    <!-- Footer -->
    <div class="bc-wizard__footer d-flex flex-wrap align-items-center justify-content-between mt-2 pt-2 border-top">
      <div>
        <b-button v-if="step > 0" variant="flat-secondary" @click="goBack" :disabled="saving">
          <feather-icon icon="ChevronLeftIcon" size="16" class="mr-25"/>
          {{ msg('Back') }}
        </b-button>
      </div>
      <div class="d-flex" style="gap:8px">
        <b-button variant="outline-secondary" @click="$emit('cancel')" :disabled="saving">
          {{ msg('Cancel') }}
        </b-button>
        <b-button v-if="step < 2" variant="primary" @click="goNext" :disabled="saving">
          <b-spinner v-if="saving" small class="mr-50"/>
          {{ step === 1 ? msg('Save building & continue') : msg('Next') }}
          <feather-icon v-if="!saving" icon="ChevronRightIcon" size="16" class="ml-25"/>
        </b-button>
        <b-button v-else variant="success" @click="finish" :disabled="saving">
          <feather-icon icon="CheckIcon" size="16" class="mr-50"/>
          {{ msg('Done') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { mapActions, mapGetters } from 'vuex'
import ClientPicker from '@/components/components/picker/clientPicker.vue'
import api from '@/services/api.service'

function emptyBuilding() {
  return {
    company: '',
    address: '',
    zip: '',
    location: '',
    gst: '',
    kg: '',
    usage: '',
    space: '',
    ownerId: null,
    buid: null,
    offerTemplateIds: [],
    propertyIds: [],
    status: 'Neubau',
    cityTax: 0,
    vat: 0,
    active: true,
  }
}

export default {
  name: 'BuildingCreateWizard',
  components: {
    ClientPicker,
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      step: 0,
      saving: false,
      building: emptyBuilding(),
      buildingId: null,
      templateList: [],
      stepMeta: [
        { key: 'loc', label: 'Details', hint: 'Address & legal' },
        { key: 'own', label: 'Assignment', hint: 'Owner & templates' },
        { key: 'prop', label: 'Properties', hint: 'Link real estate' },
      ],
      availableProperties: [],
      linkedSnapshot: [],
      propertySearch: '',
      propertiesLoading: false,
      linkingId: null,
      unlinkingId: null,
    }
  },
  computed: {
    ...mapGetters('data', ['getAllBranches']),
    ...mapGetters('building', ['allStatuse']),
    ...mapGetters('appConfig', ['isRTL']),
    ...mapGetters('translator', ['msg']),
    allBranches() {
      return Object.values(this.getAllBranches || {})
    },
    allTemplates() {
      return (this.templateList || []).map(t => ({ id: t.id, name: t.name }))
    },
    allStatus() {
      return this.allStatuse.map(s => ({ value: s.value, text: s.text.toUpperCase() }))
    },
    filteredAvailable() {
      const q = (this.propertySearch || '').trim().toLowerCase()
      const list = this.availableProperties
      if (!q) return list
      return list.filter(p => {
        const label = this.propertyLabel(p).toLowerCase()
        const addr = this.formatAddress(p).toLowerCase()
        return label.includes(q) || addr.includes(q)
      })
    },
  },
  methods: {
    ...mapActions('contractTemplate', { _loadTemplateList: 'getAllTemplates' }),
    ...mapActions('building', { _addBuilding: 'addBuilding', _updateBuilding: 'updateBuilding' }),
    ...mapActions('realEstate', { _getAllRealEstates: 'getAllRealEstates', _getRealEstateData: 'getData' }),

    reset() {
      this.step = 0
      this.saving = false
      this.building = emptyBuilding()
      const branches = this.allBranches
      if (branches.length === 1) {
        this.building.buid = branches[0].id
      }
      this.buildingId = null
      this.templateList = []
      this.availableProperties = []
      this.linkedSnapshot = []
      this.propertySearch = ''
      this.propertiesLoading = false
      this.linkingId = null
      this.unlinkingId = null
      this.loadTemplatesForBranch()
    },

    onBranchChange() {
      this.loadTemplatesForBranch()
    },

    loadTemplatesForBranch() {
      const buid = this.building.buid
      if (buid == null) return
      const filterNew = {
        search: '',
        like: { name: '', shortDescription: '' },
        eq: { type: '', buid, template: true },
        lte: {},
        gte: {},
      }
      this._loadTemplateList({ amount: 9999, filter: filterNew })
        .then(ans => {
          this.templateList = ans.Templates || []
        })
        .catch(() => { this.templateList = [] })
    },

    async goNext() {
      if (this.step === 0) {
        const ok = await this.$refs.obs0.validate()
        if (!ok) return
        this.step = 1
        this.loadTemplatesForBranch()
        return
      }
      if (this.step === 1) {
        const ok = await this.$refs.obs1.validate()
        if (!ok) return
        await this.saveBuildingAndContinue()
      }
    },

    goBack() {
      if (this.step > 0) this.step -= 1
    },
    goToStep(idx) {
      if (idx === this.step) return
      if (idx > this.step) return
      this.step = idx
    },

    async saveBuildingAndContinue() {
      this.saving = true
      try {
        const payload = { ...this.building }
        if (this.buildingId) {
          payload.id = this.buildingId
          await this._updateBuilding(payload)
        } else {
          const id = await this._addBuilding(payload)
          this.buildingId = id
          this.building.id = id
          if (!this.building.propertyIds) this.building.propertyIds = []
        }
        this.step = 2
        await this.refreshPropertiesLists()
      } catch (e) {
        this.$store.dispatch('notifications/error', {
          title: this.msg('Building'),
          message: e && e.message ? e.message : String(e),
        }, { root: true })
      } finally {
        this.saving = false
      }
    },

    async refreshPropertiesLists() {
      if (!this.building.buid || !this.buildingId) return
      this.propertiesLoading = true
      try {
        await this._getRealEstateData(false).catch(() => {})
        this.$store.commit('data/setSelectedBranches', [this.building.buid])
        const ans = await this._getAllRealEstates({
          page: 1,
          amount: 500,
          filter: {
            eq: { buid: this.building.buid },
            like: {},
            lte: {},
            gte: {},
            in: {},
          },
          sortcolumn: ['name'],
          sortby: 'asc',
        })
        const all = ans.RealEstates || []
        const branchId = Number(this.building.buid)
        const bldId = String(this.buildingId)
        const sameBranch = p => p.buid != null && Number(p.buid) === branchId
        const unlinked = p => {
          const bid = p.buildingId
          return bid == null || String(bid).trim() === ''
        }
        this.linkedSnapshot = all.filter(
          p => sameBranch(p) && p.buildingId && String(p.buildingId) === bldId
        )
        const linkedIds = new Set(this.linkedSnapshot.map(p => p.id))
        this.availableProperties = all.filter(
          p => sameBranch(p) && unlinked(p) && !linkedIds.has(p.id)
        )
      } catch (e) {
        this.$store.dispatch('notifications/error', {
          title: this.msg('Properties'),
          message: e && e.message ? e.message : String(e),
        }, { root: true })
      } finally {
        this.propertiesLoading = false
      }
    },

    propertyLabel(p) {
      if (!p) return ''
      return p.name || p.shortDescription || ('#' + (p.id || '').toString().slice(0, 8))
    },

    formatAddress(p) {
      if (!p || !p.address) return ''
      const a = p.address
      if (typeof a === 'string') return a
      return [a.street, a.zip, a.city, a.country].filter(Boolean).join(', ')
    },

    async linkProperty(p) {
      if (!this.buildingId || !p || !p.id) return
      this.linkingId = p.id
      try {
        const res = await api.sendRequest(
          'building/addPropertyToBuilding/' + this.buildingId + '/' + p.id,
          null,
          null
        )
        const b = res && res.Building
        if (b && b.propertyIds) {
          this.building.propertyIds = Array.from(b.propertyIds || [])
        }
        this.linkedSnapshot = [...this.linkedSnapshot, { ...p, buildingId: this.buildingId }]
        this.availableProperties = this.availableProperties.filter(x => x.id !== p.id)
        this.$store.dispatch('notifications/success', {
          title: this.msg('Saved'),
          message: this.msg('Property linked to building'),
        }, { root: true })
      } catch (e) {
        this.$store.dispatch('notifications/error', {
          title: this.msg('Link'),
          message: e && e.message ? e.message : String(e),
        }, { root: true })
      } finally {
        this.linkingId = null
      }
    },

    async unlinkProperty(p) {
      if (!this.buildingId || !p || !p.id) return
      this.unlinkingId = p.id
      try {
        await api.sendRequest(
          'building/removeProperty/' + this.buildingId + '/' + p.id,
          null,
          null
        )
        this.linkedSnapshot = this.linkedSnapshot.filter(x => x.id !== p.id)
        const rest = { ...p, buildingId: null }
        this.availableProperties = [...this.availableProperties, rest]
        this.$store.dispatch('notifications/success', {
          title: this.msg('Saved'),
          message: this.msg('Property unlinked'),
        }, { root: true })
      } catch (e) {
        this.$store.dispatch('notifications/error', {
          title: this.msg('Unlink'),
          message: e && e.message ? e.message : String(e),
        }, { root: true })
      } finally {
        this.unlinkingId = null
      }
    },

    finish() {
      this.$emit('done', { id: this.buildingId })
    },
  },
}
</script>

<style lang="scss" scoped>
.bc-wizard__head {
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #ebe9f1;
}
.bc-wizard__main-title {
  font-weight: 700;
  font-size: 1.05rem;
  color: #5e5873;
  margin-bottom: 0.25rem;
}
.bc-wizard__subtitle {
  font-size: 0.8125rem;
  color: #6e6b7b;
  margin: 0;
}
.bc-wizard__layout {
  align-items: flex-start;
}
/* Vertical step rail */
.bc-wizard__rail {
  flex: 0 0 auto;
  width: 100%;
  max-width: 100%;
}
.bc-wizard__rail-inner {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.bc-wizard__rail-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  width: 100%;
  text-align: left;
  border: 1px solid transparent;
  background: #f8f9fa;
  padding: 0.5rem 0.5rem;
  border-radius: 8px;
  transition: background 0.15s, border-color 0.15s;
}
.bc-wizard__rail-item:hover:not(:disabled) {
  background: #f3f2f7;
}
.bc-wizard__rail-item.active {
  background: rgba(115, 103, 240, 0.1);
  border-color: rgba(115, 103, 240, 0.35);
}
.bc-wizard__rail-item.done:not(.active) {
  opacity: 0.85;
}
.bc-wizard__rail-item:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.bc-wizard__rail-num {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  flex-shrink: 0;
  color: #5e5873;
}
.bc-wizard__rail-item.active .bc-wizard__rail-num {
  background: #7367f0;
  color: #fff;
}
.bc-wizard__rail-item.done .bc-wizard__rail-num {
  background: #28c76f;
  color: #fff;
}
.bc-wizard__rail-text {
  min-width: 0;
  display: block;
}
.bc-wizard__rail-label {
  display: block;
  font-weight: 600;
  font-size: 0.8rem;
  line-height: 1.2;
  color: #5e5873;
}
.bc-wizard__rail-hint {
  display: block;
  font-size: 0.68rem;
  color: #a5a3ae;
  line-height: 1.2;
  margin-top: 0.1rem;
}
@media (min-width: 768px) {
  .bc-wizard__rail {
    width: 148px;
    max-width: 148px;
  }
}
@media (max-width: 767px) {
  .bc-wizard__rail-inner {
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: 0.25rem;
    -webkit-overflow-scrolling: touch;
    gap: 0.5rem;
  }
  .bc-wizard__rail-item {
    min-width: 112px;
    flex-shrink: 0;
  }
}
.bc-wizard__main-col {
  min-width: 0;
}
.bc-wizard__body {
  min-height: 220px;
}
.bc-wizard__section {
  padding: 0.75rem 0.85rem;
  background: #fafafa;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
}
.bc-wizard__section-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #a5a3ae;
  margin-bottom: 0.65rem;
}
.bc-wizard__row {
  margin-left: -0.35rem;
  margin-right: -0.35rem;
}
.bc-wizard__row > [class*='col'] {
  padding-left: 0.35rem;
  padding-right: 0.35rem;
}
.bc-wizard__title {
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.85rem;
  display: flex;
  align-items: center;
  color: #5e5873;
}
.bc-wizard__panel {
  min-height: 0;
}
.bc-wizard__vselect ::v-deep .vs__dropdown-toggle {
  min-height: 2.1rem;
  padding: 0.1rem 0.35rem;
}
.bc-wizard__prop-row {
  padding: 0.55rem 0.65rem;
  border-bottom: 1px solid #eee;
}
.bc-wizard__prop-row:last-child {
  border-bottom: none;
}
.bc-wizard__prop-list {
  max-height: 260px;
  overflow-y: auto;
  background: #fff;
}
.bc-wizard__chip {
  max-width: 100%;
}
/* Empty state (replaces alert-light so text contrasts in light + dark layout) */
.bc-wizard__empty-hint {
  padding: 0.65rem 0.75rem;
  border-radius: 0.357rem;
  border: 1px solid #ebe9f1;
  background: #f8f8f8;
  color: #5e5873;
}
.cursor-pointer {
  cursor: pointer;
}

.dark-layout {
  .bc-wizard__head {
    border-bottom-color: #3b4253;
  }
  .bc-wizard__main-title,
  .bc-wizard__rail-label,
  .bc-wizard__title {
    color: #d0d2d6;
  }
  .bc-wizard__subtitle {
    color: #b4b7bd;
  }
  .bc-wizard__rail-item {
    background: #283046;
  }
  .bc-wizard__rail-item:hover:not(:disabled) {
    background: #323d55;
  }
  .bc-wizard__rail-item.active {
    background: rgba(115, 103, 240, 0.15);
  }
  .bc-wizard__section {
    background: #283046;
    border-color: #3b4253;
  }
  .bc-wizard__section-label {
    color: #8d8a94;
  }
  .bc-wizard__prop-row {
    border-bottom-color: #3b4253;
  }
  .bc-wizard__prop-list {
    background: #283046;
  }
  .bc-wizard__empty-hint {
    background: #283046;
    border-color: #3b4253;
    color: #d0d2d6;
  }
}
</style>
